import { SupportPolicy } from '@provision/entity-types';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';

const isPrimitiveValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const toPrimitiveString = (value?: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (isPrimitiveValue(value)) {
    return String(value);
  }
  return null;
};

export const normalizeSupportPolicyIds = (value?: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => toPrimitiveString(item))
      .filter((item): item is string => item !== null)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        const parsedArray = parsed as unknown[];
        return parsedArray
          .map((item) => toPrimitiveString(item))
          .filter((item): item is string => item !== null)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
    } catch {
      // ignore parse errors and fall back to comma-separated parsing
    }

    const sanitized = trimmed.replace(/^\{|\}$/g, '').replace(/^\[|\]$/g, '');
    if (!sanitized) return [];

    return sanitized
      .split(',')
      .map((item) => item.replace(/^"+|"+$/g, '').trim())
      .filter((item) => item.length > 0);
  }

  const primitive = toPrimitiveString(value);
  return primitive === null ? [] : [primitive.trim()];
};

export type NormalizedSupportPolicy = Omit<
  SupportPolicy,
  'addressIds' | 'clientIds' | 'clientToAddressIds'
> & {
  addressIds: string[];
  clientIds: string[];
  clientToAddressIds: string[];
};

export const normalizeSupportPolicyRecord = (
  policy: SupportPolicy,
): NormalizedSupportPolicy => ({
  ...policy,
  addressIds: normalizeSupportPolicyIds(policy.addressIds),
  clientIds: normalizeSupportPolicyIds(policy.clientIds),
  clientToAddressIds: normalizeSupportPolicyIds(policy.clientToAddressIds),
});

export const normalizeSupportPolicyScopeFields = (
  policy?: Partial<SupportPolicy> | Partial<NormalizedSupportPolicy>,
): {
  addressIds: string[];
  clientIds: string[];
  clientToAddressIds: string[];
} => ({
  addressIds: normalizeSupportPolicyIds(policy?.addressIds),
  clientIds: normalizeSupportPolicyIds(policy?.clientIds),
  clientToAddressIds: normalizeSupportPolicyIds(policy?.clientToAddressIds),
});

export const buildSupportPolicyPartnerFilterValue = (partnerId?: number | null): string => {
  const ids = new Set<number>();

  if (!isNullish(partnerId)) {
    ids.add(partnerId);
  }

  ids.add(PLATFORM_PARTNER_ID);

  return Array.from(ids).join(',');
};
